import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * BottomSheet — a simple full-width modal that slides up from the bottom.
 * Used for create-event, create-chore, create-poll, and detail views.
 *
 * Rendered via createPortal so ancestors with backdrop-filter / transform
 * (e.g. TopBar's backdrop-blur) cannot trap our `fixed inset-0` positioning.
 *
 * `fullHeight` pins the sheet from 32px below the top to the bottom of the
 * visible viewport (above the keyboard via the wrapper's padding-bottom).
 * We use absolute top+bottom positioning instead of `height: calc(100dvh-…)`
 * because `dvh` resolution on iOS Capacitor WebView is inconsistent on
 * cold starts — `top/bottom` anchoring is rock-solid across iOS/Android/web
 * and produces no visible "sharp cut-off" edge under the sheet's bottom.
 *
 * For non-fullHeight sheets we also round the bottom corners so a short
 * sheet doesn't end with a flat, harsh edge mid-screen.
 */
export function BottomSheet({ open, onClose, title, children, footer = null, fullHeight = false }) {
  if (!open) return null;
  return createPortal(
    <div
      data-testid="bottom-sheet-backdrop"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground/40 backdrop-blur-sm"
      style={{
        // Lift the entire bottom-anchored container ABOVE the keyboard.
        // When --kb=0 (kb closed), this collapses to nothing and the
        // sheet docks at the home indicator as usual. When the keyboard
        // is open, the sheet sits flush against the top of the keyboard.
        paddingBottom: "var(--kb, 0px)",
      }}
      onClick={onClose}
    >
      <div
        data-testid="bottom-sheet"
        className={[
          "w-full max-w-[480px] bg-background shadow-2xl pb-safe flex flex-col",
          // Always round the TOP corners. Round the BOTTOM too when the
          // sheet doesn't pin to the bottom of the viewport, so a short
          // sheet has a finished card silhouette instead of a flat edge.
          fullHeight
            ? "rounded-t-3xl"
            : "rounded-3xl mb-2",
        ].join(" ")}
        style={
          fullHeight
            ? {
                // Pin to top (32px below status bar) and bottom of the
                // viewport. Flex container above is items-end so this
                // overrides only what we need.
                position: "absolute",
                top: 32,
                bottom: 0,
                left: 0,
                right: 0,
                margin: "0 auto",
              }
            : {
                maxHeight: `calc(92dvh - var(--kb, 0px))`,
              }
        }
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-background/95 backdrop-blur rounded-t-3xl px-5 py-4 flex items-center justify-between border-b border-border/60">
          <h2 className="font-display text-[17px] font-medium tracking-tight">{title}</h2>
          <button
            data-testid="bottom-sheet-close"
            onClick={onClose}
            className="w-9 h-9 -mr-2 flex items-center justify-center text-muted-foreground tap"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div
          className="flex-1 overflow-y-auto px-5 py-4"
          style={{ WebkitOverflowScrolling: "touch" }}
        >{children}</div>
        {footer && <footer className="px-5 py-4 border-t border-border/60">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
